import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  PageHeader,
  Table,
  Drawer,
  BadgePill,
  EmptyState,
} from '@/components/campus';
import {
  DollarSign,
  Users,
  TrendingUp,
  Clock,
  Plus,
  MoreHorizontal,
  Eye,
  Loader2,
  Mail,
  Ban,
  Download,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Calendar,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const statusConfig = {
  outstanding: { label: 'Outstanding', variant: 'warning', icon: Clock },
  paid: { label: 'Paid', variant: 'success', icon: CheckCircle },
  waived: { label: 'Waived', variant: 'default', icon: Ban },
};

// Format helpers
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

// ==================== STATS CARDS ====================
const StatsCard = ({ icon: Icon, label, value, subValue, color = 'electric-ice-blue' }) => (
  <div className="p-5 bg-graphite/50 rounded-xl border border-white/[0.06] hover:border-white/[0.12] transition-colors">
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
      {subValue && (
        <span className="text-xs text-soft-silver bg-white/[0.05] px-2 py-1 rounded-full">
          {subValue}
        </span>
      )}
    </div>
    <div className="mt-4">
      <p className="text-2xl font-semibold text-cloud-white">{value}</p>
      <p className="text-sm text-soft-silver mt-1">{label}</p>
    </div>
  </div>
);

// ==================== CREATE DUE FORM ====================
const CreateDueForm = ({ onSubmit, onCancel, isLoading }) => {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [applicableStatuses, setApplicableStatuses] = useState(['active']);
  const [errors, setErrors] = useState({});

  const statusOptions = [
    { value: 'active', label: 'Active Members' },
    { value: 'pledge', label: 'Pledges' },
    { value: 'alumni', label: 'Alumni' },
  ];

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (!amount || parseFloat(amount) <= 0) newErrors.amount = 'Valid amount is required';
    if (!dueDate) newErrors.dueDate = 'Due date is required';
    if (applicableStatuses.length === 0) newErrors.statuses = 'Select at least one status';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      name,
      amount: parseFloat(amount),
      due_date: dueDate,
      applicable_statuses: applicableStatuses,
    });
  };

  const toggleStatus = (status) => {
    setApplicableStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Due Name */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Due Name *</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Spring 2026 Membership Dues"
          className="bg-obsidian border-white/[0.08] text-cloud-white"
          data-testid="due-name-input"
        />
        {errors.name && <p className="text-signal-red text-xs">{errors.name}</p>}
      </div>

      {/* Amount */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Amount *</Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-soft-silver">$</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="150.00"
            className="bg-obsidian border-white/[0.08] text-cloud-white pl-7"
            data-testid="due-amount-input"
          />
        </div>
        {errors.amount && <p className="text-signal-red text-xs">{errors.amount}</p>}
      </div>

      {/* Due Date */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Due Date *</Label>
        <Input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="bg-obsidian border-white/[0.08] text-cloud-white"
          data-testid="due-date-input"
        />
        {errors.dueDate && <p className="text-signal-red text-xs">{errors.dueDate}</p>}
      </div>

      {/* Applicable Statuses */}
      <div className="space-y-3">
        <Label className="text-soft-silver">Applicable To *</Label>
        <div className="space-y-2">
          {statusOptions.map(opt => (
            <div
              key={opt.value}
              className="flex items-center space-x-3 p-3 bg-obsidian/50 rounded-lg border border-white/[0.06] cursor-pointer hover:border-white/[0.12]"
              onClick={() => toggleStatus(opt.value)}
            >
              <Checkbox
                checked={applicableStatuses.includes(opt.value)}
                onCheckedChange={() => toggleStatus(opt.value)}
                className="border-white/[0.2] data-[state=checked]:bg-electric-ice-blue"
              />
              <span className="text-cloud-white">{opt.label}</span>
            </div>
          ))}
        </div>
        {errors.statuses && <p className="text-signal-red text-xs">{errors.statuses}</p>}
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 border-white/[0.08] text-soft-silver hover:text-cloud-white"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
          disabled={isLoading}
          data-testid="create-due-submit-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : 'Create Due'}
        </Button>
      </div>
    </form>
  );
};

// ==================== DUE DETAIL VIEW ====================
const DueDetailView = ({ due, stats, memberDues, onSendReminder, onWaive, onExport, isAdmin, isProcessing }) => {
  if (!due || !stats) return null;

  return (
    <div className="space-y-6">
      {/* Due Info Header */}
      <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
        <h3 className="text-lg font-semibold text-cloud-white">{due.name}</h3>
        <div className="flex items-center gap-4 mt-2 text-sm text-soft-silver">
          <span className="flex items-center gap-1">
            <DollarSign className="w-4 h-4" />
            {formatCurrency(due.amount)}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            Due: {formatDate(due.due_date)}
          </span>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 bg-cool-mint/10 rounded-lg border border-cool-mint/20">
          <p className="text-cool-mint text-2xl font-semibold">{formatCurrency(stats.total_collected)}</p>
          <p className="text-soft-silver text-sm">Collected</p>
        </div>
        <div className="p-4 bg-soft-amber/10 rounded-lg border border-soft-amber/20">
          <p className="text-soft-amber text-2xl font-semibold">{formatCurrency(stats.total_outstanding)}</p>
          <p className="text-soft-silver text-sm">Outstanding</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-soft-silver">Collection Progress</span>
          <span className="text-electric-ice-blue font-medium">{stats.collection_rate}%</span>
        </div>
        <Progress value={stats.collection_rate} className="h-2 bg-graphite" />
        <p className="text-xs text-soft-silver">
          {stats.paid_count} of {stats.total_members} members paid
        </p>
      </div>

      {/* Export Button (Treasurer) */}
      {isAdmin && (
        <Button
          onClick={onExport}
          variant="outline"
          className="w-full border-white/[0.08] text-soft-silver hover:text-cloud-white"
          disabled={isProcessing}
        >
          <Download className="w-4 h-4 mr-2" />
          Export to CSV
        </Button>
      )}

      {/* Member Dues List */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-soft-silver">Members</h4>
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {memberDues.map(md => (
            <div
              key={md.id}
              className="flex items-center justify-between p-3 bg-obsidian/50 rounded-lg border border-white/[0.06]"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-electric-ice-blue/10 flex items-center justify-center">
                  <Users className="w-4 h-4 text-electric-ice-blue" />
                </div>
                <div>
                  <p className="text-cloud-white text-sm">{md.member_name}</p>
                  <p className="text-muted-text text-xs">{md.member_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <BadgePill variant={statusConfig[md.status]?.variant || 'default'}>
                  {statusConfig[md.status]?.label || md.status}
                </BadgePill>
                {isAdmin && md.status === 'outstanding' && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreHorizontal className="w-4 h-4 text-muted-text" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-graphite border-white/[0.08]">
                      <DropdownMenuItem
                        onClick={() => onSendReminder(md.id)}
                        className="text-cloud-white hover:bg-white/[0.05]"
                        disabled={isProcessing}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Send Reminder
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-white/[0.08]" />
                      <DropdownMenuItem
                        onClick={() => onWaive(md.id)}
                        className="text-soft-amber hover:bg-white/[0.05]"
                        disabled={isProcessing}
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        Waive Due
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== MEMBER DUES VIEW ====================
const MemberDuesView = ({ userDues, onPay, isProcessing }) => {
  if (!userDues || userDues.length === 0) {
    return (
      <EmptyState
        icon="check"
        title="No Dues Assigned"
        description="You don't have any dues to pay at this time."
      />
    );
  }

  return (
    <div className="space-y-4">
      {userDues.map(due => {
        const StatusIcon = statusConfig[due.status]?.icon || Clock;
        const isPaid = due.status === 'paid';
        const isWaived = due.status === 'waived';
        const canPay = due.status === 'outstanding';

        return (
          <div
            key={due.member_due_id}
            className={cn(
              "p-5 rounded-xl border transition-colors",
              isPaid && "bg-cool-mint/5 border-cool-mint/20",
              isWaived && "bg-graphite/50 border-white/[0.06]",
              canPay && "bg-graphite/50 border-soft-amber/20 hover:border-soft-amber/40"
            )}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-medium text-cloud-white">{due.due_name}</h3>
                <div className="flex items-center gap-3 mt-2 text-sm text-soft-silver">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Due: {formatDate(due.due_date)}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className={cn(
                  "text-2xl font-semibold",
                  isPaid && "text-cool-mint",
                  isWaived && "text-soft-silver line-through",
                  canPay && "text-cloud-white"
                )}>
                  {formatCurrency(due.amount)}
                </p>
                <BadgePill variant={statusConfig[due.status]?.variant || 'default'} className="mt-2">
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig[due.status]?.label || due.status}
                </BadgePill>
              </div>
            </div>

            {isPaid && due.paid_at && (
              <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center gap-2 text-sm text-cool-mint">
                <CheckCircle className="w-4 h-4" />
                Paid on {formatDate(due.paid_at)}
              </div>
            )}

            {canPay && (
              <div className="mt-4 pt-4 border-t border-white/[0.06]">
                <Button
                  onClick={() => onPay(due.member_due_id)}
                  className="w-full bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
                  disabled={isProcessing}
                  data-testid={`pay-due-${due.member_due_id}`}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pay Now
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ==================== MAIN DUES PAGE ====================
export const Dues = () => {
  const { organization, session, orgRole } = useAuth();
  const { hasOrgRole } = usePermissions();

  // Check permissions
  const isAdmin = hasOrgRole(['owner', 'admin']);

  // State
  const [activeTab, setActiveTab] = useState(isAdmin ? 'admin' : 'my-dues');
  const [dues, setDues] = useState([]);
  const [orgStats, setOrgStats] = useState(null);
  const [userDues, setUserDues] = useState([]);
  const [selectedDue, setSelectedDue] = useState(null);
  const [selectedDueStats, setSelectedDueStats] = useState(null);
  const [memberDues, setMemberDues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch dues list
  const fetchDues = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/dues/list/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setDues(data.dues || []);
      }
    } catch (error) {
      console.error('Failed to fetch dues:', error);
    }
  }, [organization?.id, session?.access_token]);

  // Fetch org stats (admin only)
  const fetchOrgStats = useCallback(async () => {
    if (!organization?.id || !session?.access_token || !isAdmin) return;

    try {
      const res = await fetch(
        `${API_URL}/api/dues/org-stats/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setOrgStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch org stats:', error);
    }
  }, [organization?.id, session?.access_token, isAdmin]);

  // Fetch user dues
  const fetchUserDues = useCallback(async () => {
    if (!organization?.id || !session?.access_token) return;

    try {
      const res = await fetch(
        `${API_URL}/api/dues/my-dues/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setUserDues(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch user dues:', error);
    }
  }, [organization?.id, session?.access_token]);

  // Initial data fetch
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchDues(), fetchOrgStats(), fetchUserDues()]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchDues, fetchOrgStats, fetchUserDues]);

  // Fetch due detail
  const fetchDueDetail = async (dueId) => {
    if (!session?.access_token) return;

    try {
      const [statsRes, membersRes] = await Promise.all([
        fetch(`${API_URL}/api/dues/stats/${dueId}`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        }),
        fetch(`${API_URL}/api/dues/members/${dueId}`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setSelectedDueStats(statsData);
      }

      if (membersRes.ok) {
        const membersData = await membersRes.json();
        setMemberDues(membersData || []);
      }
    } catch (error) {
      console.error('Failed to fetch due detail:', error);
    }
  };

  // Create due
  const handleCreateDue = async (data) => {
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/dues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          ...data,
          organization_id: organization.id,
        }),
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`Due created! ${result.members_created} members assigned.`);
        setIsCreateOpen(false);
        fetchDues();
        fetchOrgStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create due');
      }
    } catch (error) {
      toast.error('Failed to create due');
    } finally {
      setIsSubmitting(false);
    }
  };

  // View due detail
  const handleViewDue = async (due) => {
    setSelectedDue(due);
    setIsDetailOpen(true);
    await fetchDueDetail(due.id);
  };

  // Send reminder
  const handleSendReminder = async (memberDueId) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/dues/remind/${memberDueId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success('Reminder sent successfully');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send reminder');
      }
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setIsProcessing(false);
    }
  };

  // Waive due
  const handleWaive = async (memberDueId) => {
    if (!window.confirm('Are you sure you want to waive this due?')) return;

    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/dues/waive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ member_due_id: memberDueId }),
      });

      if (res.ok) {
        toast.success('Due waived successfully');
        if (selectedDue) {
          await fetchDueDetail(selectedDue.id);
        }
        fetchOrgStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to waive due');
      }
    } catch (error) {
      toast.error('Failed to waive due');
    } finally {
      setIsProcessing(false);
    }
  };

  // Export CSV
  const handleExport = async () => {
    if (!selectedDue) return;

    try {
      const res = await fetch(`${API_URL}/api/dues/export/${selectedDue.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dues_${selectedDue.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('CSV exported successfully');
      } else {
        toast.error('Failed to export CSV');
      }
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  // Pay due
  const handlePayDue = async (memberDueId) => {
    setIsProcessing(true);

    try {
      const successUrl = `${window.location.origin}/app/dues?payment=success`;
      const cancelUrl = `${window.location.origin}/app/dues?payment=cancelled`;

      const res = await fetch(`${API_URL}/api/dues/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          member_due_id: memberDueId,
          success_url: successUrl,
          cancel_url: cancelUrl,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to start payment');
      }
    } catch (error) {
      toast.error('Failed to start payment');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle payment callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    const memberDueId = urlParams.get('member_due_id');

    if (payment === 'success' && sessionId) {
      // Verify payment with backend
      const verifyPayment = async () => {
        try {
          const res = await fetch(`${API_URL}/api/dues/payment-success`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({ session_id: sessionId }),
          });

          if (res.ok) {
            toast.success('Payment successful! Thank you.');
            fetchUserDues();
            fetchOrgStats();
          }
        } catch (error) {
          console.error('Payment verification error:', error);
        }
      };

      if (session?.access_token) {
        verifyPayment();
      }

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (payment === 'cancelled') {
      toast.info('Payment was cancelled');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [session?.access_token, fetchUserDues, fetchOrgStats]);

  // Table columns for dues list
  const columns = [
    {
      key: 'name',
      label: 'Due Name',
      render: (value, row) => (
        <div>
          <p className="font-medium text-cloud-white">{value}</p>
          <p className="text-xs text-soft-silver mt-1">
            {row.applicable_statuses?.join(', ') || 'All members'}
          </p>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (value) => (
        <span className="text-electric-ice-blue font-medium">{formatCurrency(value)}</span>
      ),
    },
    {
      key: 'due_date',
      label: 'Due Date',
      render: (value) => (
        <span className="text-soft-silver font-mono text-sm">{formatDate(value)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      sortable: false,
      render: (_, row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleViewDue(row)}
          className="text-electric-ice-blue hover:text-electric-ice-blue/80"
        >
          <Eye className="w-4 h-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading dues...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dues-page">
      {/* Page Header */}
      <PageHeader
        title="Dues Management"
        subtitle="Track and manage membership dues"
        actions={
          isAdmin && (
            <Button
              onClick={() => setIsCreateOpen(true)}
              className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
              data-testid="create-due-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Due
            </Button>
          )
        }
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-graphite/50 border border-white/[0.06]">
          {isAdmin && (
            <TabsTrigger value="admin" className="data-[state=active]:bg-electric-ice-blue/20">
              Admin Dashboard
            </TabsTrigger>
          )}
          <TabsTrigger value="my-dues" className="data-[state=active]:bg-electric-ice-blue/20">
            My Dues
          </TabsTrigger>
        </TabsList>

        {/* Admin Dashboard Tab */}
        {isAdmin && (
          <TabsContent value="admin" className="space-y-6 mt-6">
            {/* Stats Cards */}
            {orgStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatsCard
                  icon={DollarSign}
                  label="Total Collected"
                  value={formatCurrency(orgStats.total_collected)}
                  color="cool-mint"
                />
                <StatsCard
                  icon={Clock}
                  label="Outstanding"
                  value={formatCurrency(orgStats.total_outstanding)}
                  color="soft-amber"
                />
                <StatsCard
                  icon={TrendingUp}
                  label="Collection Rate"
                  value={`${orgStats.collection_rate}%`}
                  color="electric-ice-blue"
                />
                <StatsCard
                  icon={Users}
                  label="Members Paid"
                  value={`${orgStats.paid_count}/${orgStats.total_count}`}
                  color="electric-ice-blue"
                />
              </div>
            )}

            {/* Collection Progress */}
            {orgStats && (
              <div className="p-5 bg-graphite/50 rounded-xl border border-white/[0.06]">
                <div className="flex justify-between mb-2">
                  <span className="text-soft-silver">Overall Collection Progress</span>
                  <span className="text-electric-ice-blue font-medium">{orgStats.collection_rate}%</span>
                </div>
                <Progress value={orgStats.collection_rate} className="h-3 bg-obsidian" />
              </div>
            )}

            {/* Dues List */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-cloud-white">All Dues</h3>
              <Table
                columns={columns}
                data={dues}
                emptyState={
                  <div className="py-12">
                    <EmptyState
                      icon="dollar-sign"
                      title="No dues created"
                      description="Create your first due to start tracking member payments."
                      actionLabel="Create Due"
                      onAction={() => setIsCreateOpen(true)}
                    />
                  </div>
                }
              />
            </div>
          </TabsContent>
        )}

        {/* My Dues Tab */}
        <TabsContent value="my-dues" className="mt-6">
          <MemberDuesView
            userDues={userDues}
            onPay={handlePayDue}
            isProcessing={isProcessing}
          />
        </TabsContent>
      </Tabs>

      {/* Create Due Drawer */}
      <Drawer
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create New Due"
        width="md"
      >
        <CreateDueForm
          onSubmit={handleCreateDue}
          onCancel={() => setIsCreateOpen(false)}
          isLoading={isSubmitting}
        />
      </Drawer>

      {/* Due Detail Drawer */}
      <Drawer
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedDue(null);
          setSelectedDueStats(null);
          setMemberDues([]);
        }}
        title="Due Details"
        width="lg"
      >
        <DueDetailView
          due={selectedDue}
          stats={selectedDueStats}
          memberDues={memberDues}
          onSendReminder={handleSendReminder}
          onWaive={handleWaive}
          onExport={handleExport}
          isAdmin={isAdmin}
          isProcessing={isProcessing}
        />
      </Drawer>
    </div>
  );
};

export default Dues;
